/**
 * The governed acts a record surface offers — the console half of #1131.
 *
 * ── nothing here decides authority, and nothing here can ───────────────────
 * Every act on screen came from `core.my_entity_actions`, and whether it is
 * takeable came from `canAct`, which the server answered with `core.can_act`.
 * There is no status comparison, no role name, no persona and no capability
 * guess in this file. That is the ruling's point 4 and it is also the only way
 * the banner #1571 shipped can stop lying: it tells people to "use the actions
 * on its surface", and until now exactly one entity in the fleet had any.
 *
 * ── the binding says WHERE; the tool registry says HOW ─────────────────────
 * `my_entity_actions` deliberately does not return the tool's `bind`. Placement
 * and invocation are two different contracts (ruling 40821 point 1: "the tool
 * remains the invocation/authorization contract; the binding owns only entity
 * placement and launch metadata"), so this joins the action to the tool spec
 * from `describe_tools()` and invokes through `specToTool` — the SAME compiled
 * path the copilot uses. Building a second invocation route in React would mean
 * two places that know how to call a governed verb, and they would drift.
 *
 * ── the identity argument is NAMED, never guessed ──────────────────────────
 * `idArgument` / `idsArgument` come from the binding. A client that inferred
 * them from a convention would send the verb something it ignores: the act runs
 * against nothing and the surface reports success. That is the whole reason the
 * registrar refuses an argument the tool does not declare.
 *
 * ── one engine, three surfaces ─────────────────────────────────────────────
 * The record header, the grid row menu and the selection command bar are the
 * same list filtered by CARDINALITY. They share a launcher rather than each
 * owning a copy, because the launch rules (route vs form, confirm, invalidate)
 * are the part that must not differ between them.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { specToTool, type EntityAction, type McpToolSpec } from '@solum/sdk'
import { Button, Form, Menu, Modal, type CommandPaletteItem, type FormField } from '@solum/ui'
import { useSolum } from '../data/client'
import { invalidateRecordSurfaces, useEntityActions, useEntityIdentities } from '../data/queries'
import { actionFormFields } from './entityActionForm'
import s from './EntityActions.module.css'
import type { TableCommand } from '@solum/experience'

/** What the launcher is being asked to act on. */
type Target = { kind: 'row'; id: string } | { kind: 'batch'; ids: string[] } | { kind: 'none' }

interface Pending { action: EntityAction; target: Target; values: Record<string, unknown> }
interface Receipt { label: string; openedId: string | null; openedEntity: string | null }

const asFormField = (f: ReturnType<typeof actionFormFields>[number]): FormField => ({
  name: f.name,
  type: f.type,
  label: f.label,
  required: f.required,
  hint: f.hint,
  options: f.options,
})

/**
 * The identity the verb is told about, keyed by the argument the BINDING names.
 * A `none` action carries no identity at all, which is the point of that
 * cardinality — it acts on the entity, not on a row.
 */
function identityArgs(action: EntityAction, target: Target): Record<string, unknown> {
  if (target.kind === 'row' && action.idArgument) return { [action.idArgument]: target.id }
  if (target.kind === 'batch' && action.idsArgument) return { [action.idsArgument]: target.ids }
  return {}
}

/**
 * The shared launch engine.
 *
 * Returns the acts for one cardinality plus the affordance that runs them, and
 * the dialog that collects arguments when the act needs any. The caller renders
 * the buttons; this owns what pressing one MEANS.
 */
function useActionLauncher(entity: string | undefined, id: string | undefined) {
  const { client } = useSolum()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const go = (to: string) => (navigate as unknown as (o: { to: string }) => void)({ to })
  const identityContracts = useEntityIdentities()
  const canOpenResult = (resultEntity: string) => (identityContracts.data ?? [])
    .some((contract) => contract.entity_type === resultEntity && contract.navigation_eligible === true)
  const openResult = (resultEntity: string, resultId: string) => {
    if (canOpenResult(resultEntity)) go(`/e/${resultEntity}/${resultId}`)
  }

  const actions = useEntityActions(entity, id)
  /* Shared with the palette and the copilot — same key, so a surface that opens
     after them pays nothing. */
  const tools = useQuery({
    queryKey: ['mcp-tools'],
    staleTime: 60_000,
    queryFn: () => client.describeTools(),
  })

  const [pending, setPending] = useState<Pending | null>(null)
  const [receipt, setReceipt] = useState<Receipt | null>(null)

  const run = useMutation({
    mutationFn: async ({ action, target, values }: Pending) => {
      const spec = (tools.data ?? []).find((t: McpToolSpec) => t.name === action.tool)
      if (!spec) {
        /* The act was offered and its tool is not in the caller's manifest. That
           is a real disagreement between two caller-scoped reads, not a state to
           paper over with a silent no-op. */
        throw new Error(`'${action.tool}' is not in your tool manifest — nothing was sent.`)
      }
      const value = await specToTool(spec).handler(client, { ...identityArgs(action, target), ...values })
      const returns = (spec.bind as { returns?: { as?: unknown; entity?: unknown } }).returns
      return {
        value,
        resultKey: typeof returns?.as === 'string' ? returns.as : null,
        resultEntity: typeof returns?.entity === 'string' ? returns.entity : null,
      }
    },
    onSuccess: async (result, v) => {
      setPending(null)
      if (entity) await invalidateRecordSurfaces(qc, entity, id && id !== 'new' ? id : undefined, { work: true })

      /* ★ A DURABLE receipt, not a toast that ages out. A governed act is a
         thing that happened; if the only trace is a notification the person may
         have looked away from, they have no way to answer "did that work?"
         thirty seconds later. The receipt names the act and, where the verb
         returned a record, offers the way to it — the act's consequence is
         usually somewhere else, and making the person hunt for it is how a
         successful act still feels like a failure. */
      const openedId = typeof result.value === 'string'
        ? result.value
        : result.resultKey && result.value && typeof result.value === 'object'
          ? String((result.value as Record<string, unknown>)[result.resultKey] ?? '') || null
          : null
      setReceipt({
        label: v.action.label,
        openedId,
        openedEntity: openedId ? (result.resultEntity ?? entity ?? null) : null,
      })
    },
  })

  /**
   * A routed act is RECORD-AWARE: the desk it opens is the desk for THIS
   * record, not the module's front page. The binding's route is a path, and a
   * record route ends at the entity — so the id is appended when the route
   * names the generic record surface for this entity, and left alone when the
   * module has declared its own desk path. Guessing further (appending an id to
   * an arbitrary desk route) would invent a url the console never registered.
   */
  const routeFor = (action: EntityAction, target: Target) => {
    const base = action.route!
    if (target.kind !== 'row') return base
    return /^\/e\/[^/]+\/?$/.test(base) ? `${base.replace(/\/$/, '')}/${target.id}` : base
  }

  /** Press an act: navigate, run it, or open the form it needs. */
  const launch = (action: EntityAction, target: Target) => {
    /* A refusal is a state the person can recover from, not a permanent banner.
       Clear it before every retry; the verb still makes the next decision. */
    run.reset()
    if (action.launch === 'route') {
      if (action.route) go(routeFor(action, target))
      return
    }
    const fields = actionFormFields(action.inputSchema, [action.idArgument, action.idsArgument])
    if (fields.length === 0 && !action.requiresConfirmation) {
      run.mutate({ action, target, values: {} })
      return
    }
    setPending({ action, target, values: {} })
  }

  return { actions, tools, pending, setPending, run, launch, receipt, setReceipt, openResult, canOpenResult }
}

/**
 * What happened, said durably.
 *
 * Deliberately not a toast: a toast is the right shape for "we could not save
 * that, try again" and the wrong shape for "this act completed and its result
 * is over there". It stays until dismissed, and where the verb returned a
 * record it carries the way to it.
 */
function ActionReceipt({ receipt, onClose, onOpen, canOpen }: {
  receipt: Receipt | null
  onClose: () => void
  onOpen: (entity: string, id: string) => void
  canOpen?: (entity: string) => boolean
}) {
  if (!receipt) return null
  return (
    <div role="status" data-testid="entity-action-receipt"
      style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center', padding: 'var(--sp-2)' }}>
      <span>{receipt.label} — done.</span>
      {receipt.openedId && receipt.openedEntity && (!canOpen || canOpen(receipt.openedEntity)) && (
        <Button size="sm" variant="ghost" data-testid="entity-action-receipt-open"
          onClick={() => onOpen(receipt.openedEntity!, receipt.openedId!)}>Open it</Button>
      )}
      <Button size="sm" variant="ghost" data-testid="entity-action-receipt-dismiss" onClick={onClose}>
        Dismiss
      </Button>
    </div>
  )
}

/** A direct (zero-field) act has no dialog to own its refusal. */
function ActionError({ run }: { run: ReturnType<typeof useActionLauncher>['run'] }) {
  if (!run.isError) return null
  return (
    <p role="alert" data-testid="entity-action-error" style={{ margin: 0, color: 'var(--crit)' }}>
      {(run.error as Error).message}
    </p>
  )
}

/** The argument/confirmation dialog. Rendered once per surface, beside its acts. */
function ActionDialog({ pending, setPending, run }: {
  pending: Pending | null
  setPending: (p: Pending | null) => void
  run: ReturnType<typeof useActionLauncher>['run']
}) {
  const fields = useMemo(
    () => (pending ? actionFormFields(pending.action.inputSchema, [pending.action.idArgument, pending.action.idsArgument]) : []),
    [pending])
  if (!pending) return null

  const count = pending.target.kind === 'batch' ? pending.target.ids.length : 0
  return (
    <Modal
      label={pending.action.label}
      onClose={() => { setPending(null); run.reset() }}
      testid="entity-action-dialog"
      width={480}
    >
      <div style={{ display: 'grid', gap: 'var(--sp-3)', padding: 'var(--sp-4)' }}>
        <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)' }}>{pending.action.label}</h2>
        {pending.action.description && (
          <p style={{ margin: 0, color: 'var(--muted)' }}>{pending.action.description}</p>
        )}
        {count > 0 && (
          <p data-testid="entity-action-count" style={{ margin: 0 }}>
            {count} selected.
          </p>
        )}
        {run.isError && (
          <p role="alert" data-testid="entity-action-error" style={{ margin: 0, color: 'var(--crit)' }}>
            {(run.error as Error).message}
          </p>
        )}
        <Form
          fields={fields.map(asFormField)}
          value={pending.values}
          onChange={(patch) => setPending({ ...pending, values: { ...pending.values, ...patch } })}
          onSubmit={() => run.mutate(pending)}
          onCancel={() => { setPending(null); run.reset() }}
          submitLabel={pending.action.label}
          busy={run.isPending}
          layout="stack"
          testid="entity-action-form"
        />
      </div>
    </Modal>
  )
}

/**
 * The palette adapter for the same governed record-action launcher used by the
 * record header and grid menu. Only the highlighted record is supplied, so the
 * browser asks one `my_entity_actions` question rather than one per result row.
 */
export function usePaletteRecordActions(entity?: string, id?: string): {
  items: CommandPaletteItem[]
  overlays: ReactNode
} {
  const { actions, pending, setPending, run, launch, receipt, setReceipt, openResult, canOpenResult } =
    useActionLauncher(entity, id)
  const items = useMemo<CommandPaletteItem[]>(() => !entity || !id ? [] : (actions.data ?? [])
    .filter((action) => action.cardinality !== 'batch')
    .map((action) => ({
      id: `record-action:${entity}:${id}:${action.tool}`,
      label: action.label,
      detail: action.description,
      kind: 'action',
      disabled: action.canAct === false || run.isPending,
      disabledReason: action.canAct === false ? 'You cannot take this action on this record.' : undefined,
      closeOnSelect: false,
      onSelect: () => launch(action, action.cardinality === 'none' ? { kind: 'none' } : { kind: 'row', id }),
    })), [actions.data, entity, id, launch, run.isPending])
  return {
    items,
    overlays: (
      <>
        <ActionDialog pending={pending} setPending={setPending} run={run} />
        {!pending && <ActionError run={run} />}
        <ActionReceipt receipt={receipt} onClose={() => setReceipt(null)}
          canOpen={canOpenResult} onOpen={openResult} />
      </>
    ),
  }
}

/**
 * The record header's governed acts (ruling point 2, first host).
 *
 * An act the caller cannot take is rendered DISABLED rather than hidden: the
 * server already named it as this record's act, and removing it would make the
 * surface differ between two colleagues looking at the same screen with no
 * explanation. `canAct` decides the state, never the presence.
 */
export function RecordActions({ entity, id }: { entity: string; id: string }): ReactNode {
  const { actions, pending, setPending, run, launch, receipt, setReceipt, openResult, canOpenResult } = useActionLauncher(entity, id)
  const offered = (actions.data ?? []).filter((a) => a.cardinality !== 'batch')
  if (offered.length === 0 && !pending && !receipt && !run.isError) return null

  return (
    <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }} data-testid="record-actions">
      {offered.map((a) => (
        <Button
          key={a.tool}
          size="sm"
          variant={a.mutates ? 'primary' : 'ghost'}
          disabled={a.canAct === false || run.isPending}
          title={a.canAct === false ? 'You cannot take this act on this record.' : a.description}
          data-testid={`record-action-${a.tool}`}
          onClick={() => launch(a, a.cardinality === 'none' ? { kind: 'none' } : { kind: 'row', id })}
        >
          {a.label}
        </Button>
      ))}
      <ActionDialog pending={pending} setPending={setPending} run={run} />
      {!pending && <ActionError run={run} />}
      <ActionReceipt receipt={receipt} onClose={() => setReceipt(null)}
        canOpen={canOpenResult} onOpen={openResult} />
    </div>
  )
}

/**
 * A collection's COMMAND BUTTONS (#2375 §3.3, commands v5).
 *
 * `commands` is the RESOLVED list (`resolveTableCommands`): the presentation
 * selected, ordered and labelled commands, and resolution already dropped any
 * whose action this caller's governed registry does not offer. What a command
 * DOES is decided here, never by the document: `quickCreate` and
 * `linkExisting` run the host's own governed seams; any other action is a
 * `cardinality = 'none'` entity act, launched through the same engine the
 * record header uses — its authority is the act's own `canAct` and the server
 * door behind it.
 *
 * At most THREE sit beside the mark — the first as the primary button, the rest
 * secondary, never text-only — and the others are returned as menu items for
 * the view-settings ⋮'s "Commands" heading. One launcher serves both, so an act
 * chosen from the ⋮ gets the same argument dialog and receipt.
 */
export const COMMANDS_BESIDE_THE_MARK = 3
export function useCollectionCommands(entity: string, commands: readonly TableCommand[], builtins: {
  quickCreate?: () => void
  linkExisting?: () => void
  /** A command's test id; hosts keep their established ones (e.g. `list-new`). */
  testIdOf?: (command: TableCommand) => string
}): { bar: ReactNode; overflow: ReactNode; all: ReactNode; count: number } {
  const { actions, pending, setPending, run, launch, receipt, setReceipt, openResult, canOpenResult } = useActionLauncher(entity, undefined)
  const byTool = new Map((actions.data ?? []).filter((a) => a.cardinality === 'none').map((a) => [a.tool, a]))
  const testIdOf = builtins.testIdOf ?? ((command: TableCommand) => `command-${command.id}`)
  /* A command is offered only when its seam is present NOW — a document may
     name an act this caller's registry lost since resolution; it is not drawn. */
  const runnable = commands.filter((command) => command.action === 'quickCreate' ? builtins.quickCreate != null
    : command.action === 'linkExisting' ? builtins.linkExisting != null
      : byTool.has(command.action))
  const act = (command: TableCommand) => {
    if (command.action === 'quickCreate') { builtins.quickCreate?.(); return }
    if (command.action === 'linkExisting') { builtins.linkExisting?.(); return }
    const action = byTool.get(command.action)
    if (action) launch(action, { kind: 'none' })
  }
  const disabledFor = (command: TableCommand) => {
    const action = byTool.get(command.action)
    return (action?.canAct === false) || run.isPending
  }
  const shown = runnable.slice(0, COMMANDS_BESIDE_THE_MARK)
  const rest = runnable.slice(COMMANDS_BESIDE_THE_MARK)
  const bar = (
    <>
      {shown.map((command, index) => (
        <Button key={command.id} size="sm" variant={index === 0 && command.kind === 'primary' ? 'primary' : 'secondary'}
          data-testid={testIdOf(command)} disabled={disabledFor(command)}
          title={byTool.get(command.action)?.canAct === false ? 'You cannot take this act here.' : undefined}
          onClick={() => act(command)}>
          {command.label}
        </Button>
      ))}
      <ActionDialog pending={pending} setPending={setPending} run={run} />
      {!pending && <ActionError run={run} />}
      <ActionReceipt receipt={receipt} onClose={() => setReceipt(null)}
        canOpen={canOpenResult} onOpen={openResult} />
    </>
  )
  const items = (list: readonly TableCommand[]) => list.length === 0 ? undefined : list.map((command) => (
    <Menu.Item key={command.id} testId={`${testIdOf(command)}-more`} disabled={disabledFor(command)}
      onSelect={() => act(command)}>
      {command.label}
    </Menu.Item>
  ))
  /* `all`: when the person hides the command buttons, the commands are not
     gone — the ⋮ lists every one of them, so hiding is presentation only. */
  return { bar, overflow: items(rest), all: items(runnable), count: runnable.length }
}

/**
 * One grid row's governed acts (ruling point 2, second host).
 *
 * A trailing overflow rather than a fourth lead control: `DataGrid`'s lead
 * column is budgeted for at most three (expand, select, pin) and a fourth would
 * need a new width token. The menu is the same list the header shows, so a
 * person who learns an act on the record finds it on the row.
 */
export function RowActions({ entity, id, allowedTools }: {
  entity: string
  id: string
  /** A card declaration may narrow placement; the server-filtered list remains authority. */
  allowedTools?: readonly string[]
}): ReactNode {
  /* ★ The per-RECORD question is asked when the menu opens, not per row.
     `canAct` is answered by `core.can_act` against one record, so asking it for
     every row of a 50-row page would be 50 authority questions to draw one ⋮ —
     and 49 of the answers would be thrown away. The entity-level list (shared
     with every other row through one cache key) says WHETHER this entity has
     acts at all; opening asks whether THIS caller may take them here. */
  const [asked, setAsked] = useState(false)
  const { pending, setPending, run, launch, receipt, setReceipt, openResult, canOpenResult } = useActionLauncher(entity, asked ? id : undefined)
  const known = useEntityActions(entity)
  const detail = useEntityActions(entity, id, { enabled: asked })
  const source = (asked && detail.data ? detail.data : known.data) ?? []
  const offered = source.filter((a) => a.cardinality !== 'batch'
    && (allowedTools == null || allowedTools.includes(a.tool)))
  if (offered.length === 0 && !pending && !receipt && !run.isError) return null

  return (
    <>
      <Menu
        testId={`row-actions-${id}`}
        trigger={(
          /* No stopPropagation here, deliberately. ListView's '__queue_pickup'
             column stops it on its own button, so I added the same call and
             asserted it mattered — then measured, and it does not: Radix's
             menu trigger already swallows the click before the row's
             onActivateRow sees it. The browser spec still asserts the record
             does not open, because that is the PROPERTY; this is a note about
             the mechanism, so nobody re-adds a guard whose arm is green. */
          /* #2184: `s.trigger` is a COARSE-POINTER-ONLY `min-width: 44px`.
             The shared button already gives this a 44px min-height, so the
             vertical half of WCAG 2.5.8 was met and the horizontal half was
             not — an icon-only `⋮` measured 26 × 44 beside 44 × 44 neighbours.
             The minimum is applied here rather than in Button.module.css
             because the gap belongs to icon-only triggers, not to `size="sm"`,
             and because fine-pointer density must not move. */
          <Button size="sm" variant="ghost" aria-label="Actions" className={s.trigger}
            onClick={() => setAsked(true)}>⋮</Button>
        )}
      >
        {offered.map((a) => (
          <Menu.Item
            key={a.tool}
            disabled={a.canAct === false}
            testId={`row-action-${a.tool}`}
            onSelect={() => launch(a, a.cardinality === 'none' ? { kind: 'none' } : { kind: 'row', id })}
          >
            {a.label}
          </Menu.Item>
        ))}
      </Menu>
      <ActionDialog pending={pending} setPending={setPending} run={run} />
      {!pending && <ActionError run={run} />}
      <ActionReceipt receipt={receipt} onClose={() => setReceipt(null)}
        canOpen={canOpenResult} onOpen={openResult} />
    </>
  )
}

/**
 * The selection's governed acts — the half the ruling did not cover and 58197
 * added.
 *
 * `cardinality: 'batch'` is the only thing that may appear here. A row act
 * looped over a selection would be a different act with the same name: no
 * transaction, partial failure, and a receipt nobody can read.
 */
export function BatchActions({ entity, ids }: { entity: string; ids: string[] }): ReactNode {
  const { actions, pending, setPending, run, launch, receipt, setReceipt, openResult, canOpenResult } = useActionLauncher(entity, undefined)
  const offered = (actions.data ?? []).filter((a) => a.cardinality === 'batch')
  if (offered.length === 0 && !pending && !receipt && !run.isError) return null

  return (
    <>
      {offered.map((a) => (
        <BatchAction key={a.tool} entity={entity} action={a} ids={ids}
          busy={run.isPending} onLaunch={() => launch(a, { kind: 'batch', ids })} />
      ))}
      <ActionDialog pending={pending} setPending={setPending} run={run} />
      {!pending && <ActionError run={run} />}
      <ActionReceipt receipt={receipt} onClose={() => setReceipt(null)}
        canOpen={canOpenResult} onOpen={openResult} />
    </>
  )
}

/**
 * One selection act, and what it will actually reach.
 *
 * ★ The button alone cannot be honest about a mixed selection. The server
 * refuses a batch ATOMICALLY — proven in the kernel suite — so pressing it on
 * five rows the caller may change three of does nothing at all, and the person
 * is left reading a refusal that names no row. Asking first turns that into a
 * sentence they can act on BEFORE they press.
 *
 * It still does not gate: the count is advice, the verb is the authority, and a
 * surface that treated `all` as permission would have misread it.
 */
function BatchAction({ entity, action, ids, busy, onLaunch }: {
  entity: string
  action: EntityAction
  ids: string[]
  busy: boolean
  onLaunch: () => void
}): ReactNode {
  const { client } = useSolum()
  const cap = useQuery({
    queryKey: ['entity-action-capability', entity, action.tool, ids.join(',')],
    enabled: ids.length > 0,
    queryFn: () => client.myEntityActionCapability(entity, action.tool, ids),
  })
  const c = cap.data
  return (
    <span style={{ display: 'inline-flex', gap: 'var(--sp-1)', alignItems: 'center' }}>
      <Button size="sm" variant="ghost" disabled={ids.length === 0 || busy}
        data-testid={`batch-action-${action.tool}`} onClick={onLaunch}>
        {action.label}
      </Button>
      {c && c.verdict === 'some' && (
        <span data-testid={`batch-capability-${action.tool}`} style={{ color: 'var(--muted)' }}>
          {c.allowed} of {c.total}
        </span>
      )}
      {c && c.verdict === 'none' && ids.length > 0 && (
        <span data-testid={`batch-capability-${action.tool}`} style={{ color: 'var(--muted)' }}>
          none of {c.total}
        </span>
      )}
    </span>
  )
}
